import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { LoadingScreen } from '../ui'

export function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return children || <Outlet />
}

export function RequireAdmin() {
  const { isAuthenticated, isAdmin, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/infra/client" replace />
  return <Outlet />
}

export function RequireClient() {
  const { isAuthenticated, isClient, isAdmin, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isClient && !isAdmin) return <Navigate to="/engage" replace />
  return <Outlet />
}

export function RequireGuest() {
  const { isAuthenticated, user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (isAuthenticated) {
    const role = user?.role
    if (role === 'admin' || role === 'super_admin') return <Navigate to="/infra/admin" replace />
    if (role === 'client') return <Navigate to="/infra/client" replace />
    return <Navigate to="/engage" replace />
  }
  return <Outlet />
}

/**
 * The platform-admin surface. Mirrors the API's `platform.role` rank gate.
 *
 * `minRank` 3 restricts a branch to super_admin and above — used for Settings,
 * which holds live secrets. A user without the rank is sent to their own home
 * rather than to /login, because they ARE signed in; bouncing them to the login
 * page would look like a broken session.
 */
export function RequirePlatformAdmin({ minRank = 2 }) {
  const { isAuthenticated, platformRank, loading, user } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (platformRank < minRank) {
    const home = user?.role === 'client' ? '/infra/client' : '/engage'
    return <Navigate to={home} replace />
  }
  return <Outlet />
}

export function RequireModule({ module }) {
  const { isAuthenticated, hasModule, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!hasModule(module)) return <Navigate to="/upgrade" replace />
  return <Outlet />
}
